import React from 'react';
import {
  Select,
  SelectMenuItem
} from '../../../../frontend-common-libs/src/components/common/dropdown';
import ProtocolListRepository from '../../../protocol/repository/ProtocolListRepository';
import { getProtocolsIfNeeded } from '../../../protocol/actions/protocol-actions';
import noneProtocolItem from '../../noneProtocolItem';
import styles from './styles/protocol-selection-dropdown.module.scss';
import FeatureFlags, {
  FeatureFlagKeys
} from '../../../../frontend-common-libs/src/components/feature_flags';

export type ProtocolListType = { name: string; id: string }[];

export type Props = {
  protocols: Map<string, any>;
  selectedProtocol: string;
  updateSelectedProtocol: (value: { id: string; name: string }) => void;
};

export async function fetchProtocolsIfNeeded(projectId: string) {
  if (!FeatureFlags().get(FeatureFlagKeys.ASSIGN_PROTOCOL_LIST)) {
    const projectProtocols = ProtocolListRepository.instance.getProjectProtocols(projectId);
    if (projectProtocols.staleData) {
      await getProtocolsIfNeeded(projectId, '');
    }
    return projectProtocols.protocols;
  }

  let projectProtocols = ProtocolListRepository.instance.getProjectProtocols(projectId);
  if (projectProtocols.lastSeen === '' && !projectProtocols.staleData) {
    // all protocols have already been fetched
    return projectProtocols.protocols;
  }

  const maxFetches = 100; // each fetch is 50 protocols or 5000 protocols total hard limit
  let currentFetches = 1; // first 50 protocols fetched.
  await getProtocolsIfNeeded(projectId, '');

  // fetch all protocols
  while (projectProtocols.lastSeen !== '' && currentFetches < maxFetches) {
    currentFetches += 1;
    // eslint-disable-next-line no-await-in-loop
    await getProtocolsIfNeeded(projectId, projectProtocols.lastSeen);
    projectProtocols = ProtocolListRepository.instance.getProjectProtocols(projectId);
  }

  return projectProtocols.protocols;
}

export default function ProtocolSelectionDropdown(props: Readonly<Props>) {
  const { selectedProtocol, updateSelectedProtocol, protocols } = props;

  const buildProtocolOptionsList = (): { id: string; name: string }[] => {
    const protocolList: ProtocolListType = [];
    protocolList.push(noneProtocolItem);
    protocols.forEach(protocol => {
      protocolList.push({
        id: protocol.get('id'),
        name: protocol.get('name')
      });
    });
    return protocolList;
  };

  const protocolList: ProtocolListType = buildProtocolOptionsList();

  return (
    <div className={styles.protocolAssignmentContainer}>
      <div className={styles.protocolAssignmentText} data-testid="protocol-assignment-text">
        Assign Protocol to Reserved Instruments (Optional)
      </div>
      <div className={styles.protocolAssignmentSelect}>
        <Select
          id="protocol-options"
          data-testid="protocol-options"
          value={selectedProtocol === '' ? noneProtocolItem.name : selectedProtocol}
          handleSelection={updateSelectedProtocol}
          menuClassName={styles.protocolAssignmentMenu}
        >
          {protocolList.map(protocol => {
            return (
              <SelectMenuItem
                id={`protocol-${protocol.id}`}
                key={protocol.id}
                value={{ id: protocol.id, name: protocol.name }}
                selected={protocol.name === selectedProtocol}
              >
                {protocol.name}
              </SelectMenuItem>
            );
          })}
        </Select>
      </div>
    </div>
  );
}
